import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Tabs,
  Tab,
  Chip,
  Autocomplete,
  CircularProgress,
  Alert,
  Typography,
  InputAdornment
} from '@mui/material';
import { PersonAdd as PersonAddIcon, Search as SearchIcon } from '@mui/icons-material';
import { searchUsers, updateUserRoles, addUser } from '../../../services/api';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function AddSupervisorModal({ open, onClose, onSuccess, token, defaultMaxTherapists = 10 }) {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Existing user search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // New user form
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    role: ['clinical_supervisor']
  });

  // Supervisor-specific fields (for both modes)
  const [supervisorInfo, setSupervisorInfo] = useState({
    specializations: [],
    maxTherapistCapacity: defaultMaxTherapists
  });

  const [specializationInput, setSpecializationInput] = useState('');

  useEffect(() => {
    if (open) {
      // Reset form when modal opens
      setTabValue(0);
      setError('');
      setSelectedUser(null);
      setSearchQuery('');
      setSearchResults([]);
      setNewUserData({
        name: '',
        email: '',
        password: '',
        role: ['clinical_supervisor']
      });
      setSupervisorInfo({
        specializations: [],
        maxTherapistCapacity: defaultMaxTherapists
      });
      setSpecializationInput('');
    }
  }, [open, defaultMaxTherapists]);

  // Debounced search for existing users
  useEffect(() => {
    if (tabValue === 0 && searchQuery.trim().length >= 2) {
      const timer = setTimeout(async () => {
        try {
          setSearchLoading(true);
          const data = await searchUsers({
            q: searchQuery,
            limit: 20
          }, token);
          setSearchResults(data.users || []);
        } catch (err) {
          console.error('Search error:', err);
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, token, tabValue]);

  const handleAddSpecialization = (event) => {
    if (event.key === 'Enter' && specializationInput.trim()) {
      event.preventDefault();
      if (!supervisorInfo.specializations.includes(specializationInput.trim())) {
        setSupervisorInfo({
          ...supervisorInfo,
          specializations: [...supervisorInfo.specializations, specializationInput.trim()]
        });
      }
      setSpecializationInput('');
    }
  };

  const handleRemoveSpecialization = (specToRemove) => {
    setSupervisorInfo({
      ...supervisorInfo,
      specializations: supervisorInfo.specializations.filter(s => s !== specToRemove)
    });
  };

  const handleSubmitExisting = async () => {
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Add clinical_supervisor role if not already present
      const updatedRoles = selectedUser.role.includes('clinical_supervisor')
        ? selectedUser.role
        : [...selectedUser.role, 'clinical_supervisor'];

      await updateUserRoles(selectedUser._id, {
        roles: updatedRoles,
        supervisorInfo
      }, token);

      onSuccess();
    } catch (err) {
      console.error('Error assigning supervisor role:', err);
      setError(err.message || 'Failed to assign supervisor role');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitNew = async () => {
    // Validate form
    if (!newUserData.name.trim() || !newUserData.email.trim() || !newUserData.password.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');

      await addUser({
        ...newUserData,
        supervisorInfo
      }, token);

      onSuccess();
    } catch (err) {
      console.error('Error creating supervisor:', err);
      setError(err.message || 'Failed to create supervisor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (tabValue === 0) {
      handleSubmitExisting();
    } else {
      handleSubmitNew();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon />
          Add Clinical Supervisor
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Assign Existing User" />
          <Tab label="Create New User" />
        </Tabs>

        {/* Tab 1: Search and assign existing user */}
        <TabPanel value={tabValue} index={0}>
          <Autocomplete
            options={searchResults}
            getOptionLabel={(option) => `${option.name} (${option.email}) - ${option.role.join(', ')}`}
            loading={searchLoading}
            value={selectedUser}
            onChange={(event, newValue) => setSelectedUser(newValue)}
            inputValue={searchQuery}
            onInputChange={(event, newInputValue) => setSearchQuery(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for user"
                placeholder="Type name or email..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Box>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email} • Roles: {option.role.join(', ')}
                  </Typography>
                </Box>
              </li>
            )}
            noOptionsText={searchQuery.length < 2 ? "Type at least 2 characters" : "No users found"}
          />

          {selectedUser && selectedUser.role.includes('clinical_supervisor') && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This user already has the clinical supervisor role. Updating their supervisor info will override existing settings.
            </Alert>
          )}
        </TabPanel>

        {/* Tab 2: Create new user */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              required
              fullWidth
              value={newUserData.name}
              onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
            />
            <TextField
              label="Password"
              type="password"
              required
              fullWidth
              value={newUserData.password}
              onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
              helperText="Minimum 6 characters"
            />
          </Box>
        </TabPanel>

        {/* Supervisor-specific fields (shown for both tabs) */}
        <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom>
            Clinical Supervisor Configuration
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Specializations */}
            <Box>
              <TextField
                label="Specializations / Areas of Expertise"
                fullWidth
                value={specializationInput}
                onChange={(e) => setSpecializationInput(e.target.value)}
                onKeyDown={handleAddSpecialization}
                helperText="Press Enter to add specialization"
                placeholder="e.g., CBT, Trauma, Anxiety, Supervision Training"
              />
              {supervisorInfo.specializations.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                  {supervisorInfo.specializations.map((spec, idx) => (
                    <Chip
                      key={idx}
                      label={spec}
                      onDelete={() => handleRemoveSpecialization(spec)}
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Max Capacity */}
            <TextField
              label="Max Therapist Capacity"
              type="number"
              fullWidth
              value={supervisorInfo.maxTherapistCapacity}
              onChange={(e) => setSupervisorInfo({ ...supervisorInfo, maxTherapistCapacity: parseInt(e.target.value) || defaultMaxTherapists })}
              inputProps={{ min: 1, max: 50 }}
              helperText="Maximum number of therapists this supervisor can oversee"
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || (tabValue === 0 && !selectedUser) || (tabValue === 1 && (!newUserData.name || !newUserData.email || !newUserData.password))}
          startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
        >
          {loading ? 'Adding...' : (tabValue === 0 ? 'Assign Role' : 'Create Supervisor')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddSupervisorModal;
